import styles from './PublicReportCampaignTable.module.css';
import { CampaignTableRow } from './types';
import { instagram, InstagramAudio } from '@round/api';
import { TableProps } from '@round/ui-kit';
import ProjectCell, {
    Meta as ProjectCellMeta,
    ProjectData,
} from 'Modules/Plans/Campaigns/components/cells/ProjectCell/ProjectCell';
import { ColumnDef } from '@tanstack/react-table';
import CampaignsTable from '../../../Campaigns/components/CampaignsTable/CampaignsTable';
import ExpandCell from '../../../Campaigns/components/cells/ExpandCell/ExpandCell';
import ReportCampaignCell from '../../../Campaigns/components/CampaignsTable/components/ReportCampaignCell/ReportCampaignCell';
import { toDecimalPoint } from '@round/utils';
import { buildInstagramAudioUrl, numberWithCommas } from 'helpers';
import CampaignStatsCell from '../../../Campaigns/components/cells/CampaignStatsCell';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from '../../../helpers';
import DurationCell from '../../../Campaigns/components/cells/DurationCell';
import { CampaignsTableMeta } from '../../../Campaigns/types';
import AudioPostGrowthCell, {
    Meta as AudioPostGrowthCellMeta,
} from 'Modules/Plans/Campaigns/components/cells/AudioPostGrowthCell';
import PostsCell from './components/PostsCell';
import ProgressCell from './components/ProgressCell';
import CampaignCostCell from './components/CampaignCostCell';
import InstagramActionsCell from './components/ActionsCell/InstagramActionsCell';

export type InstagramPublicReportCampaignTableRow = CampaignTableRow & {
    audioTimeSeries?: instagram.TimeSeriesResponse<'posts_daily_change'>[number] | null;
    audio?: InstagramAudio | null;
};

export type Meta = CampaignsTableMeta &
    AudioPostGrowthCellMeta<InstagramPublicReportCampaignTableRow> &
    Pick<
        ProjectCellMeta<InstagramPublicReportCampaignTableRow>,
        'getIsBrandLoading' | 'getIsProjectLoading' | 'getIsTeamLoading'
    >;

type Props = Pick<
    TableProps<InstagramPublicReportCampaignTableRow>,
    'data' | 'expandedState' | 'onExpandedChange' | 'renderSubComponent'
> & {
    hasError?: boolean;
    isLoading: boolean;
    meta: Meta;
};

const InstagramPublicReportCampaignTable = ({
    data,
    isLoading,
    hasError,
    expandedState,
    onExpandedChange,
    renderSubComponent,
    meta,
}: Props) => {
    const columns: ColumnDef<InstagramPublicReportCampaignTableRow, any>[] = [
        {
            header: '',
            id: 'expand',
            cell: ExpandCell,
        },
        {
            header: 'Project',
            id: 'project',
            accessorFn: (row): ProjectData => ({
                name: row.project?.name ?? '-',
                image: row.project?.image,
                brandImage: row.brand?.image,
                brandName: row.brand?.name ?? '-',
                teamName: row.team?.name ?? '-',
            }),
            cell: ProjectCell,
        },
        {
            header: 'Campaign',
            accessorKey: 'name',
            meta: {
                className: styles.campaignColumn,
            },
            cell: ReportCampaignCell,
        },
        {
            header: 'Posts',
            id: 'posts',
            cell: PostsCell,
        },
        {
            header: 'Progress',
            id: 'progress',
            cell: ProgressCell,
        },
        {
            header: 'Cost',
            id: 'cost',
            cell: CampaignCostCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => (row.stats?.view_count ? numberWithCommas(row.stats.view_count) : '-'),
            cell: CampaignStatsCell,
        },
        {
            header: () => (
                <TooltipHeader header="ER" tooltipTitle="Engagement Rate" tooltipBody={tooltipCopy.engagementRate} />
            ),
            id: 'er',
            accessorFn: (row) =>
                typeof row.stats?.engagement_rate === 'number'
                    ? `${toDecimalPoint(row.stats.engagement_rate * 100, 2)}%`
                    : '-',
            cell: CampaignStatsCell,
        },
        {
            header: 'Duration',
            id: 'duration',
            cell: DurationCell,
        },
        {
            header: 'Audio',
            id: 'audio',
            accessorFn: (row) => ({
                audioUrl: row.audio ? buildInstagramAudioUrl(row.audio.instagram_id) : undefined,
                timeSeries: row.audioTimeSeries,
            }),
            meta: { className: styles.audioGraph },
            cell: AudioPostGrowthCell,
        },
        {
            header: '',
            id: 'actions',
            meta: { className: styles.actionsCell },
            cell: InstagramActionsCell,
        },
    ];

    return (
        <CampaignsTable
            className={styles.table}
            data={data}
            columns={columns}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            expandedState={expandedState}
            onExpandedChange={onExpandedChange}
            onRowClick={(row) => {
                if (typeof expandedState === 'object') {
                    onExpandedChange?.({ ...expandedState, [row.id]: !expandedState[row.id] });
                }
            }}
            renderSubComponent={renderSubComponent}
            columnVisibility={{
                cost: data.some((d) => 'cost' in d),
                audio: data.some((d) => d.audioTimeSeries),
            }}
        />
    );
};

export default InstagramPublicReportCampaignTable;
